import { type CSSProperties } from 'react';

import { ClickerUnit } from '@/entities/user/model/user.types';
import { type Prize, WHEEL_PRIZES } from '@/entities/wheel/model/wheel.types';

import { BoosterSize, BoosterType } from '@/shared/ui/booster/booster.types';
import { BoosterIcon } from '@/shared/ui/booster/ui/BoosterIcon';
import { type AnyIcon } from '@/shared/ui/icon';
import { XPIcon } from '@/shared/ui/xp-icon/XPIcon';

import { ReactComponent as ClickerIcon } from '@/assets/images/clicker.svg?react';
import { ReactComponent as Luck } from '@/assets/images/luck.svg?react';

import MainCoin from '@/assets/lotties/prizes/coin_main.lottie?url';
import ConfettiCoin from '@/assets/lotties/prizes/confetti_coin.lottie?url';
import GlareStar from '@/assets/lotties/prizes/glare_star.lottie?url';
import RotatingStar from '@/assets/lotties/prizes/rotating_star.lottie?url';
import FullTicketLottie from '@/assets/lotties/prizes/ticket.lottie?url';
import HalfTicketLottie from '@/assets/lotties/prizes/ticket-half.lottie?url';
import TonLottie from '@/assets/lotties/prizes/ton_coin.lottie?url';

// Use <img/> to avoid of glitches in Safari
import NotCoin from '@/assets/images/notcoin-prize.svg';

import { GradientVariables } from '@/tokens';

export const CONFIG_BY_PRIZE_TYPE: Record<
  Prize['type'],
  {
    pageStyles: CSSProperties;
    image:
      | { lottie: string | [string, string] }
      | { icon: ((prize: Prize) => AnyIcon) | AnyIcon }
      | null;
  }
> = {
  [WHEEL_PRIZES.PARTNER]: {
    image: { icon: () => XPIcon },
    pageStyles: {
      background: GradientVariables.GRADIENT_BRAND_13,
    },
  },
  [WHEEL_PRIZES.TON]: {
    image: { lottie: TonLottie },
    pageStyles: {
      background: GradientVariables.GRADIENT_BRAND_13,
    },
  },
  [WHEEL_PRIZES.TICKET]: {
    image: { lottie: [HalfTicketLottie, FullTicketLottie] },
    pageStyles: {
      background: GradientVariables.GRADIENT_BRAND_12,
    },
  },
  [WHEEL_PRIZES.PREMIUM]: {
    image: { lottie: [GlareStar, RotatingStar] },
    pageStyles: {
      background: GradientVariables.GRADIENT_TG_PREMIUM,
    },
  },
  [WHEEL_PRIZES.PLAYCOIN]: {
    image: { lottie: [MainCoin, ConfettiCoin] },
    pageStyles: {
      background: GradientVariables.GRADIENT_BRAND_1,
    },
  },
  [WHEEL_PRIZES.XP]: {
    image: { icon: () => XPIcon },
    pageStyles: {
      background: GradientVariables.GRADIENT_BRAND_1,
    },
  },
  [WHEEL_PRIZES.NOT]: {
    image: { icon: () => NotCoin },
    pageStyles: {
      background: GradientVariables.GRADIENT_BRAND_9,
    },
  },
  [WHEEL_PRIZES.LOOTBOX]: {
    image: null,
    pageStyles: {
      background: GradientVariables.GRADIENT_BRAND_9,
    },
  },
  [WHEEL_PRIZES.BOOSTER_PLAYCOIN]: {
    image: {
      icon: () => (
        <BoosterIcon type={BoosterType.BOOSTER_PLAYCOIN} size={BoosterSize.LARGE} />
      ),
    },
    pageStyles: {
      background:
        'radial-gradient(102.54% 56.57% at 50% 38.06%, #E2586A 15.5%, #E9982F 100%)',
    },
  },
  [WHEEL_PRIZES.BOOSTER_XP]: {
    image: {
      icon: () => <BoosterIcon type={BoosterType.BOOSTER_XP} size={BoosterSize.LARGE} />,
    },
    pageStyles: {
      background:
        'background: radial-gradient(102.54% 56.57% at 50% 38.06%, #E2586A 15.5%, #E9982F 100%)',
    },
  },
  [WHEEL_PRIZES.CLICKER_PLAYCOIN]: {
    image: {
      icon: () => ClickerIcon,
    },
    pageStyles: {
      background: GradientVariables.GRADIENT_BRAND_3,
    },
  },
  [WHEEL_PRIZES.CLICKER_XP]: {
    image: {
      icon: () => ClickerIcon,
    },
    pageStyles: {
      background: GradientVariables.GRADIENT_BRAND_3,
    },
  },
  [WHEEL_PRIZES.LUCK]: {
    image: {
      icon: () => Luck,
    },
    pageStyles: {
      background: GradientVariables.GRADIENT_BRAND_7,
    },
  },
  [WHEEL_PRIZES.PHYSICAL]: {
    image: { icon: (prize: Prize) => prize.image },
    pageStyles: {
      background: GradientVariables.GRADIENT_BRAND_13,
    },
  },
};

export const CLICKER_UNIT_BY_PRIZE_TYPE: Record<
  WHEEL_PRIZES.CLICKER_PLAYCOIN | WHEEL_PRIZES.CLICKER_XP,
  ClickerUnit
> = {
  [WHEEL_PRIZES.CLICKER_PLAYCOIN]: ClickerUnit.PLAYCOIN,
  [WHEEL_PRIZES.CLICKER_XP]: ClickerUnit.XP,
};
